import React from 'react';
import styled from 'styled-components';
import {
  ToggleButton as MuiButtonBase,
  ToggleButtonProps,
  Tooltip,
} from '@mui/material';
import { componentToken } from '../../style/utils';

const ToggleIconButton = styled(MuiButtonBase)`
  gap: ${componentToken('segmentedButton.toggleIconButton.gap')};
  color: ${componentToken('segmentedButton.toggleIconButton.color')};
  border-radius: ${componentToken(
    'segmentedButton.toggleIconButton.borderRadius',
  )} !important;
  display: flex;
  justify-content: center;
  align-items: center;

  /* Icon-only selection tiles need more room than regular buttons */
  &.MuiToggleButton-sizeSmall {
    width: 32px;
    height: 32px;
    padding: ${componentToken('segmentedButton.toggleIconButton.smallPadding')};
    min-width: 32px;
    min-height: 32px;
  }

  &.MuiToggleButton-sizeMedium {
    width: 40px;
    height: 40px;
    padding: ${componentToken(
      'segmentedButton.toggleIconButton.mediumPadding',
    )};
    min-width: 40px;
    min-height: 40px;
  }

  &.MuiToggleButton-sizeLarge {
    width: 48px;
    height: 48px;
    padding: ${componentToken('segmentedButton.toggleIconButton.largePadding')};
    min-width: 48px;
    min-height: 48px;
  }

  :hover {
    filter: grayscale(0) !important;
    background-blend-mode: normal !important;
    background-color: unset;
    border: 1.5px solid
      ${componentToken('segmentedButton.toggleIconButton.hoverBorder')} !important;
  }

  &.Mui-selected {
    background-color: ${componentToken(
      'segmentedButton.toggleIconButton.selectedBackground',
    )};
    filter: grayscale(0);
    background-blend-mode: normal;
    color: ${componentToken('segmentedButton.toggleIconButton.selectedColor')};
    border: 1.5px solid
      ${componentToken('segmentedButton.toggleIconButton.selectedBorder')} !important;

    /* Default box-shadow (applied when no size class is present) */
    box-shadow: ${componentToken(
      'segmentedButton.toggleIconButton.selectedShadow',
    )};

    /* Size-specific box shadows */
    &.MuiToggleButton-sizeSmall {
      box-shadow: ${componentToken(
        'segmentedButton.toggleIconButton.selectedShadowSmall',
      )};
    }

    &.MuiToggleButton-sizeMedium {
      box-shadow: ${componentToken(
        'segmentedButton.toggleIconButton.selectedShadowMedium',
      )};
    }

    &.MuiToggleButton-sizeLarge {
      box-shadow: ${componentToken(
        'segmentedButton.toggleIconButton.selectedShadow',
      )};
    }

    :hover {
      background-color: ${componentToken(
        'segmentedButton.toggleIconButton.selectedBackground',
      )};
    }
  }
`;

type TooltipToggleButtonProps = {
  title: string;
  children: React.ReactNode;
} & ToggleButtonProps;

const SegmentedButton = React.forwardRef(
  (
    { children, title, size = 'medium', ...props }: TooltipToggleButtonProps,
    ref: any,
  ) => (
    <Tooltip title={title}>
      <ToggleIconButton ref={ref} size={size} {...props}>
        {children}
      </ToggleIconButton>
    </Tooltip>
  ),
);

export default SegmentedButton;
